<template>
  <div class="row main-content">
    <div class="col">
      <div
        v-if="isProcessing"
        class="text-center"
      >
        <img
          src="@/assets/img/loader.gif"
          class="mx-auto d-block center-block"
        >
      </div>

      <div v-else>
        <h1 class="page-title font-custom text-uppercase mt-3">
          {{ $t("title.product.create") }}
        </h1>

        <div
          v-if="responseErr"
          class="alert alert-danger text-center border-0 shadow-sm rounded-0"
        >
          <strong>
            {{ $t("error." + `${responseErr}`) }}
          </strong>
        </div>
        <div
          class="card-body bg-light mb-1 d-flex justify-content-end"
        >
          <button
            type="button"
            class="base-button font-inverted font-custom rounded-pill btn btn-primary btn-export me-1 ms-1"
            @click="importProduct()"
          >
            {{ $t("button.import") }}
          </button>
          <button
            type="button"
            class="base-button font-custom rounded-pill btn btn-primary btn-export me-1 ms-1"
            @click="handleExport(product['product'])"
          >
            {{ $t("button.export") }}
          </button>
        </div>
        <div
          v-if="$store.state.products.violations"
          class="row"
        >
          <button
            class="right-el base-button font-custom rounded-pill btn btn-primary font-inverted btn-export me-3 mt-2 mb-2"
            @click="showError(modalError, modalDescription)"
          >
            {{ $t('button.displayErrors') }}
          </button>
        </div>
        <div class="card-body mb-1 d-flex justify-content-end pt-1 pb-1">
          <div class="text-end pb-2">
            {{ $t('label.product.createdProductsLastMonth') }}
          </div>
          <div class="pb-2 me-3 ms-1 fw-bold">
            {{ $filters.nullIsZero(createdProductsLastMonth) }}
          </div>
        </div>
        <div class="card-body mb-1 d-flex justify-content-end pt-1 pb-1">
          <div class="text-end pb-2">
            {{ $t('label.product.createdProductsThisMonth') }}
          </div>
          <div class="pb-2 me-3 ms-1 fw-bold">
            {{ $filters.nullIsZero(createdProductsThisMonth) }}
          </div>
        </div>

        <div v-if="productStructure">
          <Form
            @submit="onSubmit"
            @invalid-submit="onInvalidSubmit"
          >
            <div
              v-for="productDescription in productStructure"
              :id="'accordion' + productDescription.code"
              :key="productDescription.id"
              class="card rounded-0 mb-4"
            >
              <div
                :id="'heading' + productDescription.code"
                data-bs-toggle="collapse"
                :href="'#collapse' + productDescription.code"
                aria-expanded="false"
                :aria-controls="'collapse' + productDescription.code"
                class="card-header font-accent fw-bold"
              >
                {{ productDescription.code }} :
                {{ productDescription.description }}
                <svg
                  v-if="blockErrors[productDescription.code]"
                  aria-hidden="true"
                  focusable="false"
                  class="float-end error-info bi bi-exclamation-triangle-fill"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
                  <title> {{ Object.values(productErrors[productDescription.fieldName]).flat().filter(Boolean).join('\n') }} </title>
                </svg>
              </div>
              <div
                :id="'collapse' + productDescription.code"
                :aria-labelledby="'heading' + productDescription.code"
                :data-bs-parent="'#accordion' + productDescription.code"
                class="card-body collapse"
              >
                <div
                  v-for="param in productDescription.params"
                  :key="param.code"
                  class="row p-1"
                >
                  <div class="col-3 text-end mt-perso">
                    <strong>
                      {{ param.code }} :
                    </strong>
                  </div>
                  <div
                    v-if="param.code == 'PRE-28'"
                    class="col-3 mt-perso"
                    style="padding-left: 0; font-style: italic;"
                  >
                    <div class="row-3">
                      <span>{{ param.description }}</span>
                      <!-- param.regexp n'est pas correct (trop de détails) à voir pour avoir un autre format -->
                      <span
                        v-if="!param.outOnly"
                        :title="$t('label.product.expectedFormat') + param.regexp"
                        data-bs-toggle="tooltip"
                        data-bs-placement="left"
                        class="label label-default ms-2"
                      >
                        <i class="fa fa-info-circle fa-xs" />
                      </span>
                      <span
                        v-for="(target, index) in param.target"
                        :key="target"
                        :class="[index == 0 ? 'ms-2' : '']"
                      >
                        <sub
                          v-if="param.target.length > 1 && index < param.target.length - 1"
                          class="label label-default fw-bold"
                        >
                          {{ target }}  /
                        </sub>
                        <sub
                          v-else
                          class="label label-default fw-bold"
                        >
                          {{ target }}
                        </sub>
                      </span>
                    </div>
                    <div
                      class="row p-1 errorMessage"
                    >
                      <span>{{ $t('label.product.infoPre28') }}</span>
                    </div>
                  </div>
                  <div
                    v-else-if="param.code == 'PRS-32' || param.code == 'PRS-33' || param.code == 'PRS-34'"
                    class="col-3 mt-perso"
                    style="padding-left: 0; font-style: italic;"
                  >
                    <div class="row-3">
                      <span>{{ param.description }}</span>
                      <!-- param.regexp n'est pas correct (trop de détails) à voir pour avoir un autre format -->
                      <span
                        v-if="!param.outOnly"
                        :title="$t('label.product.expectedFormat') + param.regexp"
                        data-bs-toggle="tooltip"
                        data-bs-placement="left"
                        class="label label-default ms-2"
                      >
                        <i class="fa fa-info-circle fa-xs" />
                      </span>
                      <span
                        v-for="(target, index) in param.target"
                        :key="target"
                        :class="[index == 0 ? 'ms-2' : '']"
                      >
                        <sub
                          v-if="param.target.length > 1 && index < param.target.length - 1"
                          class="label label-default fw-bold"
                        >
                          {{ target }}  /
                        </sub>
                        <sub
                          v-else
                          class="label label-default fw-bold"
                        >
                          {{ target }}
                        </sub>
                      </span>
                    </div>
                    <div
                      class="row p-1 errorMessage"
                    >
                      <span>{{ $t('label.product.infoPrs323334') }}</span>
                    </div>
                  </div>
                  <div
                    v-else
                    class="col-3 mt-perso"
                    style="padding-left: 0; font-style: italic;"
                  >
                    <span>{{ param.description }} </span>
                    <!-- param.regexp n'est pas correct (trop de détails) à voir pour avoir un autre format -->
                    <span
                      v-if="!param.outOnly"
                      :title="$t('label.product.expectedFormat') + param.regexp"
                      data-bs-toggle="tooltip"
                      data-bs-placement="left"
                      class="label label-default ms-2"
                    >
                      <i class="fa fa-info-circle fa-xs" />
                    </span>
                    <span
                      v-for="(target, index) in param.target"
                      :key="target"
                      :class="[index == 0 ? 'ms-2' : '']"
                    >
                      <sub
                        v-if="param.target.length > 1 && index < param.target.length - 1"
                        class="label label-default fw-bold"
                      >
                        {{ target }}  /
                      </sub>
                      <sub
                        v-else
                        class="label label-default fw-bold"
                      >
                        {{ target }}
                      </sub>
                    </span>
                  </div>
                  <div class="col">
                    <Field
                      v-if="param.code != 'PRB-7' && !param.outOnly && param.format != 'BLOCK'"
                      v-model="product['product'][productDescription.fieldName][param.fieldName]"
                      class="form-control rounded-0 form-control-sm"
                      :name="param.code"
                      type="text"
                      :rules="{required: param.mandatory, regex: param.regexp}"
                      @input="updateErrors(productDescription.fieldName, param.code, product['product'][productDescription.fieldName][param.fieldName], param)"
                      @blur="updateErrors(productDescription.fieldName, param.code, product['product'][productDescription.fieldName][param.fieldName], param)"
                    />
                    <Field
                      v-else-if="!param.outOnly && param.format == 'BLOCK'"
                      v-model="product['product'][productDescription.fieldName][param.fieldName]"
                      class="form-control rounded-0 form-control-sm"
                      :name="param.code"
                      as="textarea"
                      rows="6"
                      :rules="{required: param.mandatory, regex: param.regexp}"
                      @input="updateErrors(productDescription.fieldName, param.code, product['product'][productDescription.fieldName][param.fieldName], param)"
                      @blur="updateErrors(productDescription.fieldName, param.code, product['product'][productDescription.fieldName][param.fieldName], param)"
                    />
                    <Field
                      v-else-if="param.outOnly"
                      class="form-control rounded-0 form-control-sm"
                      :name="param.code"
                      type="text"
                      disabled
                      :title="$t('label.product.outOnly')"
                    />
                    <div v-else>
                      <div
                        v-if="!isDistributorLocked"
                      >
                        <div class="row mb-2">
                          <!-- Distributor -->
                          <div class="col">
                            <Field
                              v-model="selectedDistributorRef"
                              as="select"
                              :disabled="isDistributorLocked"
                              :name="param.code"
                              class="form-control rounded-0"
                              placeholder="$t('label.product.distributorRef')"
                              :rules="{required: param.mandatory}"
                              @input="updateErrors(productDescription.fieldName, param.code, selectedDistributorRef, param)"
                              @blur="updateErrors(productDescription.fieldName, param.code, selectedDistributorRef, param)"
                            >
                              <option
                                value
                                selected
                                disabled
                                hidden
                              >
                                {{ $t('label.product.distributorRef') }}
                              </option>
                              <option
                                v-for="distributor in distributors"
                                :key="distributor.id"
                                :value="distributor"
                              >
                                {{ distributor }}
                              </option>
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>
                    <ErrorMessage
                      v-slot="{ message }"
                      :name="param.code"
                    >
                      <p class="errorMessage">
                        {{ message }}
                      </p>
                    </ErrorMessage>
                  </div>
                </div>
              </div>
            </div>
            <template v-if="hasPermission('product:create')">
              <div
                id="cardBottom"
                class="card-body bg-light d-flex flex-row-reverse align-items-center mb-4"
              >
                <button
                  class="base-button font-custom rounded-pill btn btn-primary font-inverted btn-export m-1"
                >
                  {{ $t('button.create') }}
                </button>
                <span
                  :title="$t('label.product.updateHours')"
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  class="label label-default me-2"
                >
                  <i class="fa fa-info-circle fa-xs pr-2" />
                </span>
                <input
                  id="updateDate"
                  v-model="updateDate"
                  type="date"
                  name="updateDate"
                  class="rounded-0 me-2"
                  placeholder="yyyy-MM-dd"
                  @input="updateErrorDate()"
                >
                <label
                  class="form-check-label fw-bold p-2"
                  for="immediate"
                >
                  {{ $t("label.product.updateDate") }}
                </label>
              </div>
              <div
                v-if="dateError"
                class="card-body bg-light mb-4 d-flex flex-row-reverse align-items-center pt-0"
              >
                <span class="error">{{ $t('error.INVALID_DATE_PRODUCT') }}</span>
              </div>
            </template>
          </Form>
        </div>
      </div>
    </div>
    <SuccessDialogProduct ref="successDialogRef" />
    <ProductErrorDialog ref="productErrorDialogRef" />
    <div
      id="copyModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="copyModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="copyModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.COPY_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-info">
              <p>
                {{ $t('label.modal.COPY_MODAL_MSG') }}
              </p>
            </div>
            <textarea
              id="copyModalText"
              class="form-control"
              rows="5"
              readonly="readonly"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      id="pasteModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="pasteModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        class="modal-dialog"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="pasteModalLabel"
              class="modal-title"
            >
              {{ $t('label.modal.PASTE_MODAL_LABEL') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div class="modal-body">
            <div class="alert alert-info">
              <p>
                {{ $t('label.modal.PASTE_MODAL_MSG') }}
              </p>
            </div>
            <textarea
              id="pasteModalText"
              v-model="jsonPastedObject"
              class="form-control"
              rows="5"
            />
          </div>
        </div>
      </div>
    </div>
    <goTopButton />
  </div>
</template>

<script src="./ProductCreate.js"></script>

<style src="./ProductCreate.scss" scoped lang="scss"></style>
